import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Send } from "lucide-react";
import { TelegramGrowthChart } from "./TelegramGrowthChart";
import { TelegramScheduledPosts } from "./TelegramScheduledPosts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/** Iscritti del canale Telegram @edicolavillaricca + crescita dagli snapshot serali. */
export const TelegramChannelWidget = () => {
  const [stats, setStats] = useState(null);
  const [postText, setPostText] = useState("");
  const [postPhoto, setPostPhoto] = useState(null);
  const [posting, setPosting] = useState(false);
  const [postResult, setPostResult] = useState("");
  const [couponCode, setCouponCode] = useState("TELEGRAM10");
  const [couponPercent, setCouponPercent] = useState(10);
  const [couponDays, setCouponDays] = useState(30);
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponResult, setCouponResult] = useState("");
  const [couponPublish, setCouponPublish] = useState(false);
  const [createdCoupon, setCreatedCoupon] = useState(null);

  const shareText = (c) =>
    `🎟️ Coupon sconto -${c.discount_value}% su Edicola Cerasuolo!\nCodice: ${c.code}\nValido fino al ${c.expiry_date || "—"}, una volta per persona (non cumulabile con lo sconto benvenuto).\n👉 https://edicolavillaricca.com/prodotti`;

  const shareCoupon = async () => {
    if (!createdCoupon) return;
    const text = shareText(createdCoupon);
    try { await navigator.clipboard.writeText(text); } catch (e) { /* clipboard may be unavailable */ }
    if (navigator.share) {
      try { await navigator.share({ title: `Coupon ${createdCoupon.code}`, text }); return; } catch (e) { /* user cancelled */ }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const createExclusiveCoupon = async () => {
    if (couponBusy) return;
    const percent = parseInt(couponPercent, 10);
    const days = parseInt(couponDays, 10);
    if (!(percent >= 1 && percent <= 50)) { setCouponResult("❌ Inserisci una percentuale tra 1 e 50"); return; }
    if (!(days >= 1 && days <= 365)) { setCouponResult("❌ Inserisci una durata tra 1 e 365 giorni"); return; }
    if (!window.confirm(`Creo il coupon ${couponCode.toUpperCase()} (-${percent}%, ${days} giorni)${couponPublish ? " e lo pubblico sul canale Telegram" : ""}?`)) return;
    setCouponBusy(true);
    setCouponResult("");
    setCreatedCoupon(null);
    try {
      const token = sessionStorage.getItem("access_token");
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/telegram/exclusive-coupon`,
        { code: couponCode, percent, days, publish: couponPublish },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCreatedCoupon(res.data.coupon);
      const exp = res.data.coupon.expiry_date;
      if (couponPublish) {
        setCouponResult(res.data.posted ? `✅ ${res.data.coupon.code} creato e pubblicato sul canale (scade ${exp})` : `✅ ${res.data.coupon.code} creato (pubblicazione sul canale non riuscita)`);
      } else {
        setCouponResult(`✅ ${res.data.coupon.code} creato (scade ${exp}). Usa "Condividi" per inviarlo.`);
      }
    } catch (e) {
      setCouponResult(`❌ ${e.response?.data?.detail || "Errore"}`);
    } finally {
      setCouponBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = sessionStorage.getItem("access_token");
        const res = await axios.get(`${BACKEND_URL}/api/admin/telegram/channel-stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setStats(res.data);
      } catch (e) {
        console.warn("[telegram-channel] fetch failed:", e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!stats || !stats.channel) return null;
  const snaps = stats.snapshots || [];
  const growth = snaps.length >= 2 ? stats.members - snaps[0].members : null;

  return (
    <Card data-testid="telegram-channel-widget">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="w-5 h-5 text-[#229ED9]" />
          Canale Telegram
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg flex-1" data-testid="telegram-members">
            <div className="text-3xl font-extrabold text-[#229ED9]">
              {stats.members >= 0 ? stats.members : "—"}
            </div>
            <div className="text-[11px] uppercase tracking-wide text-gray-500 mt-1">Iscritti</div>
            {growth != null && (
              <div className={`text-[11px] font-bold mt-1 ${growth >= 0 ? "text-green-600" : "text-red-500"}`} data-testid="telegram-growth">
                {growth >= 0 ? `▲ +${growth}` : `▼ ${growth}`}{" "}
                <span className="font-normal text-gray-400">ultimi {snaps.length} gg</span>
              </div>
            )}
          </div>
          <div className="flex-1 text-sm text-gray-500">
            <a
              href={`https://t.me/${stats.channel.replace("@", "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#229ED9] font-semibold hover:underline"
              data-testid="telegram-channel-link"
            >
              {stats.channel} ↗
            </a>
            <p className="text-[11px] text-gray-400 mt-2">
              Ogni nuovo articolo e ogni promozione con banner vengono pubblicati automaticamente sul canale.
            </p>
          </div>
        </div>
        <TelegramGrowthChart snapshots={snaps} />
        {stats.clicks && (
          <div className="mt-4 border-t pt-4" data-testid="telegram-clicks-section">
            <p className="text-xs font-semibold text-gray-600 mb-2">🖱️ Click sull'invito Telegram dal sito</p>
            <div className="grid grid-cols-4 gap-2">
              {[["Oggi", stats.clicks.today, "today"], ["7 giorni", stats.clicks.week, "week"], ["30 giorni", stats.clicks.month, "month"], ["Totale", stats.clicks.total, "total"]].map(([label, val, key]) => (
                <div key={key} className="text-center bg-gray-50 rounded-lg py-2" data-testid={`telegram-clicks-${key}`}>
                  <div className="text-lg font-extrabold text-[#229ED9]">{val}</div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
                </div>
              ))}
            </div>
            {stats.clicks.by_source?.length > 0 && (
              <p className="text-[11px] text-gray-400 mt-2" data-testid="telegram-clicks-sources">
                {stats.clicks.by_source.map((s) => `${{ home_popup: "popup home", home_social: "icone social home", top_bar: "barra in alto" }[s.source] || s.source}: ${s.n}`).join(" · ")}
              </p>
            )}
          </div>
        )}
        <div className="mt-4 border-t pt-4">
          <p className="text-xs font-semibold text-gray-600 mb-2">✍️ Scrivi un post sul canale</p>
          <textarea
            value={postText}
            onChange={(e) => setPostText(e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder="Es. Domani in negozio arrivano le nuove carte Pokémon! Passate a trovarci 🎉"
            className="w-full text-sm border rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#229ED9]/40"
            data-testid="telegram-post-textarea"
          />
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <label className="cursor-pointer text-xs font-semibold text-gray-600 border border-dashed border-gray-300 hover:border-[#229ED9] rounded-lg px-3 py-2 transition-colors">
              📷 {postPhoto ? "Cambia foto" : "Allega foto"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="telegram-post-photo-input"
                onChange={(e) => setPostPhoto(e.target.files?.[0] || null)}
              />
            </label>
            {postPhoto && (
              <span className="flex items-center gap-2 text-xs text-gray-600 bg-gray-100 rounded-lg px-2 py-1" data-testid="telegram-post-photo-preview">
                <img src={URL.createObjectURL(postPhoto)} alt="anteprima" className="w-8 h-8 object-cover rounded" />
                {postPhoto.name.slice(0, 24)}
                <button onClick={() => setPostPhoto(null)} className="text-red-500 font-bold ml-1" data-testid="telegram-post-photo-remove">✕</button>
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-2">
            <button
              onClick={async () => {
                if ((!postText.trim() && !postPhoto) || posting) return;
                setPosting(true);
                setPostResult("");
                try {
                  const token = sessionStorage.getItem("access_token");
                  const fd = new FormData();
                  fd.append("text", postText.trim());
                  if (postPhoto) fd.append("photo", postPhoto);
                  await axios.post(`${BACKEND_URL}/api/admin/telegram/channel-message`, fd, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  setPostResult("✅ Pubblicato sul canale!");
                  setPostText("");
                  setPostPhoto(null);
                } catch (e) {
                  setPostResult(`❌ ${e.response?.data?.detail || "Errore di pubblicazione"}`);
                } finally {
                  setPosting(false);
                  setTimeout(() => setPostResult(""), 5000);
                }
              }}
              disabled={(!postText.trim() && !postPhoto) || posting}
              className="bg-[#229ED9] hover:bg-[#1c86b8] disabled:opacity-40 text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors"
              data-testid="telegram-post-submit-btn"
            >
              {posting ? "Pubblico…" : "📢 Pubblica sul canale"}
            </button>
            {postResult && (
              <span className="text-xs font-semibold" data-testid="telegram-post-result">{postResult}</span>
            )}
          </div>
        </div>
        <div className="mt-4 border-t pt-4" data-testid="telegram-coupon-section">
          <p className="text-xs font-semibold text-gray-600 mb-1">🎟️ Coupon esclusivo iscritti</p>
          <p className="text-[11px] text-gray-400 mb-2">
            Valido una sola volta per email e <strong>non cumulabile</strong> con lo sconto di benvenuto: chi lo usa non riceve il coupon benvenuto registrandosi, e viceversa. Non appare sul sito.
          </p>
          <div className="flex items-end gap-2 flex-wrap">
            <label className="text-[11px] text-gray-500">Codice
              <input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} maxLength={20}
                className="block w-32 text-sm border rounded-lg px-2 py-1.5 mt-0.5" data-testid="telegram-coupon-code-input" />
            </label>
            <label className="text-[11px] text-gray-500">Sconto %
              <div className="flex items-center gap-1 mt-0.5">
                {[5, 10].map((p) => (
                  <button key={p} type="button" onClick={() => setCouponPercent(p)}
                    className={`text-xs font-bold px-2 py-1.5 rounded-lg border ${Number(couponPercent) === p ? "bg-pink-600 text-white border-pink-600" : "bg-white text-gray-700 border-gray-300"}`}
                    data-testid={`telegram-coupon-percent-${p}`}>{p}%</button>
                ))}
                <input type="number" min={1} max={50} inputMode="numeric" value={couponPercent} onChange={(e) => setCouponPercent(e.target.value)}
                  className="block w-16 text-sm border rounded-lg px-2 py-1.5" data-testid="telegram-coupon-percent-input" />
              </div>
            </label>
            <label className="text-[11px] text-gray-500">Giorni
              <input type="number" min={1} max={365} inputMode="numeric" value={couponDays} onChange={(e) => setCouponDays(e.target.value)}
                className="block w-20 text-sm border rounded-lg px-2 py-1.5 mt-0.5" data-testid="telegram-coupon-days-input" />
            </label>
            <button onClick={createExclusiveCoupon} disabled={couponBusy || !couponCode.trim()}
              className="bg-pink-600 hover:bg-pink-700 disabled:opacity-40 text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors"
              data-testid="telegram-coupon-create-btn">
              {couponBusy ? "Creo…" : "🎟️ Crea coupon"}
            </button>
          </div>
          <label className="flex items-center gap-2 mt-2 text-xs text-gray-600 cursor-pointer select-none">
            <input type="checkbox" checked={couponPublish} onChange={(e) => setCouponPublish(e.target.checked)} className="accent-pink-600" data-testid="telegram-coupon-publish-checkbox" />
            Pubblica anche sul canale Telegram
          </label>
          {couponResult && <p className="text-xs font-semibold mt-2" data-testid="telegram-coupon-result">{couponResult}</p>}
          {createdCoupon && (
            <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="telegram-coupon-share-box">
              <span className="font-mono text-sm font-bold bg-pink-50 text-pink-700 border border-pink-200 px-2 py-1 rounded" data-testid="telegram-coupon-created-code">{createdCoupon.code}</span>
              <button type="button" onClick={shareCoupon}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg"
                data-testid="telegram-coupon-share-btn">
                📤 Condividi (copia + WhatsApp/app)
              </button>
            </div>
          )}
        </div>
        <TelegramScheduledPosts />
      </CardContent>
    </Card>
  );
};
